import { useNavigate } from 'react-router';
import useEmblaCarousel from 'embla-carousel-react';
import { ArrowRight } from 'lucide-react';
import { Skeleton } from '@heroui/react';
import { useTranslation } from 'react-i18next';
import {
  DIALOGUE_HISTORY_CACHE_KEY,
  dialogueTitle,
  fetchDialogues,
  formatDialogueWhen,
  toIntlLocale,
  type DialogueSummary,
} from '@/entities/dialogue';
import { TUTORS, TutorAvatar } from '@/entities/tutor';
import { ROUTES } from '@/shared/config';
import { haptics } from '@/shared/lib/telegram/haptics';
import { useCachedQuery } from '@/shared/lib/useCachedQuery';
import type { Page } from '@/shared/lib/usePagedQuery';
import { cn } from '@/shared/lib/cn';
import { useInDesktopShell } from '@/shared/lib/desktopLayout';
import { ErrorBlock } from '@/shared/ui';

/** Сколько последних разговоров показываем — сама история (`HistoryPage`) без ограничения, тут только превью. */
const RECENT_LIMIT = 3;

/**
 * Первая страница истории — тот же кеш, из которого `HistoryPage` начинает
 * свой список, так что карусели хватает её одной. Наружу — для `HomePage`:
 * ей надо знать, не упали ли разом все блоки главной.
 */
export function useRecentDialogues() {
  return useCachedQuery<Page<DialogueSummary>>(DIALOGUE_HISTORY_CACHE_KEY, () => fetchDialogues());
}

/**
 * Секция «Предыдущие диалоги» на главном экране: горизонтальная эмбла-карусель
 * (тот же приём, что у «Свободная практика»/«Рулетка тем» —
 * `widgets/home-promo-carousel`), а не список строк, как раньше. Первый слайд
 * — не диалог, а квадратная кнопка-стрелка на всю историю (`HistoryPage`):
 * та же высота, что у карточек диалога (`size-20` у обоих), только квадратная,
 * а не вытянутая. Дальше — сами карточки на всю ширину экрана (`basis-full`,
 * та же ширина, что была у них строкой до перехода на карусель), `dragFree` —
 * можно остановиться в любом месте ленты, а не только на снапе. Тот же
 * кеш-ключ, что и у `HistoryPage` (`DIALOGUE_HISTORY_CACHE_KEY`) — переход
 * туда не показывает лишний раз скелетон, если список уже загружен здесь.
 */
export function RecentDialoguesCarousel() {
  const { t } = useTranslation('RecentDialoguesCarousel');
  const navigate = useNavigate();
  const { data, error, reload } = useRecentDialogues();
  const [emblaRef] = useEmblaCarousel({ align: 'start', dragFree: true });
  const desktop = useInDesktopShell();

  const openAll = () => {
    haptics.advance();
    void navigate(ROUTES.history);
  };
  const openDialogue = (id: string) => void navigate(`${ROUTES.chat}/${id}`);

  // Нет разговоров — блока нет. А вот сбой показываем: молча пропавший блок
  // читался как «так и задумано».
  if (error != null) {
    return (
      <div className="flex flex-col gap-3">
        <h2 className="text-lg font-bold text-ink">{t('heading')}</h2>
        <ErrorBlock onRetry={reload} />
      </div>
    );
  }
  if (data !== null && data.items.length === 0) return null;

  const items = data?.items.slice(0, RECENT_LIMIT) ?? null;

  // На компьютере — сетка 2×2 вместо ленты: три разговора и плитка «Все
  // диалоги» той же высоты. Ленту мышью листать неудобно.
  if (desktop) {
    return (
      <div className="flex flex-col gap-3">
        <h2 className="text-lg font-bold text-ink">{t('heading')}</h2>
        <div className="grid grid-cols-2 gap-3">
          {items === null
            ? Array.from({ length: RECENT_LIMIT }, (_, index) => <DialogueCardSkeleton key={index} inGrid />)
            : items.map((item) => (
                <DialogueCard key={item.id} item={item} onOpen={() => openDialogue(item.id)} inGrid />
              ))}
          <button
            type="button"
            onClick={openAll}
            className="flex h-20 items-center justify-center gap-2 rounded-2xl border border-hairline bg-bg text-[15px] font-semibold text-ink transition-colors hover:bg-glass-2"
          >
            {t('allDialoguesAria')}
            <ArrowRight className="size-5" aria-hidden="true" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <h2 className="text-lg font-bold text-ink">{t('heading')}</h2>

      <div className="overflow-hidden" ref={emblaRef}>
        <div className="-ml-2 flex touch-pan-y">
          <div className="shrink-0 grow-0 pl-2">
            <button
              type="button"
              onClick={openAll}
              aria-label={t('allDialoguesAria')}
              className="flex size-20 items-center justify-center rounded-2xl border border-hairline bg-bg transition-transform active:scale-[0.97] active:bg-glass-2"
            >
              <ArrowRight className="size-5 text-ink" aria-hidden="true" />
            </button>
          </div>

          {items === null
            ? Array.from({ length: RECENT_LIMIT }, (_, index) => (
                <DialogueCardSkeleton key={index} />
              ))
            : items.map((item) => (
                <DialogueCard key={item.id} item={item} onOpen={() => openDialogue(item.id)} />
              ))}
        </div>
      </div>
    </div>
  );
}

function DialogueCard({ item, onOpen, inGrid = false }: { item: DialogueSummary; onOpen: () => void; inGrid?: boolean }) {
  const { t, i18n } = useTranslation('RecentDialoguesCarousel');
  const tutor = TUTORS.find((candidate) => candidate.id === item.tutor.id);
  const title = dialogueTitle(item, i18n.language, {
    lesson: t('lessonTitle'),
    freeConversation: t('freeConversationTitle'),
  });
  const when = formatDialogueWhen(item.lastActiveAt, toIntlLocale(i18n.language), t('yesterday'));

  return (
    <div className={inGrid ? 'min-w-0' : 'min-w-0 shrink-0 grow-0 basis-full pl-2'}>
      <button
        type="button"
        onClick={() => {
          haptics.advance();
          onOpen();
        }}
        className="flex h-20 w-full items-center gap-3 rounded-2xl bg-glass p-3 text-left transition-transform active:scale-[0.97] active:bg-glass-2"
      >
        {tutor ? (
          <TutorAvatar
            tutor={tutor}
            image="portrait"
            className="size-14 shrink-0 rounded-full"
            emojiClassName="text-2xl"
          />
        ) : (
          <div className="size-14 shrink-0 rounded-full bg-glass-2" aria-hidden="true" />
        )}
        <div className="flex min-w-0 flex-col gap-0.5">
          <p className="truncate text-[16px] font-semibold text-ink">
            {item.tutor.name} · {title}
          </p>
          <p className="text-[13px] text-ink-subtle">{when}</p>
        </div>
      </button>
    </div>
  );
}

function DialogueCardSkeleton({ inGrid = false }: { inGrid?: boolean }) {
  return (
    <div className={cn('min-w-0', !inGrid && 'shrink-0 grow-0 basis-full pl-2')}>
      <div className="flex h-20 w-full items-center gap-3 rounded-2xl bg-glass p-3">
        <Skeleton className="size-14 shrink-0 rounded-full" />
        <div className="flex flex-col gap-1.5">
          <Skeleton className="h-4 w-28" />
          <Skeleton className="h-3 w-20" />
        </div>
      </div>
    </div>
  );
}
